<script setup lang="ts">
import { Search as SearchIcon, X as XIcon } from '@lucide/vue'
import { computed, ref, useAttrs } from 'vue'

/**
 * Inline search field — leading search icon + input + built-in clear. v-model'able; exposes
 * `inputEl` for callers that focus it. `inheritAttrs` is off and split by hand: `class`/`style`
 * stay on the wrapper (so `class="flex-1"` sizes the field), while every other attr/listener
 * (data-testid, aria-label, enterkeyhint, @keydown/@focus/@blur) forwards to the <input>. Esc clears.
 *
 * `size`: md (h-8, inline toolbars/lists — the default) · lg (h-11 rounded-2xl white card with
 * shadow — the pinned mobile search bar). Consolidates the library / collection / mobile search
 * inputs. (The floating map-search pill is a launch affordance, not a static input — it stays.)
 */
interface Props {
  modelValue?: string
  placeholder?: string
  size?: 'md' | 'lg'
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: 'Search…',
  size: 'md',
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const inputEl = ref<HTMLInputElement | null>(null)
defineExpose({ inputEl })

const lg = computed(() => props.size === 'lg')
const inputClass = computed(() =>
  lg.value
    ? 'h-11 w-full rounded-2xl border border-gray-200 bg-white pl-10 pr-10 text-sm shadow-[0_4px_20px_rgba(15,23,42,0.12)] focus:outline-none focus:ring-1 focus:ring-primary-500'
    : 'h-8 w-full rounded-xl border border-gray-200 bg-gray-50 pl-8 pr-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary-500',
)
</script>

<template>
  <div class="relative w-full" :class="attrs.class" :style="attrs.style as never">
    <SearchIcon
      class="pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400"
      :class="lg ? 'left-3 h-5 w-5' : 'left-3 h-3.5 w-3.5'"
    />
    <input
      ref="inputEl"
      v-bind="inputAttrs"
      type="text"
      :value="props.modelValue"
      :placeholder="props.placeholder"
      :class="inputClass"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      @keydown.esc.prevent="emit('update:modelValue', '')"
    />
    <button
      v-if="props.modelValue"
      type="button"
      class="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:cursor-pointer hover:text-gray-600 focus:outline-none"
      :class="lg ? 'right-3' : 'right-2'"
      title="Clear search"
      @click="emit('update:modelValue', '')"
    >
      <XIcon :class="lg ? 'h-5 w-5' : 'h-4 w-4'" />
    </button>
  </div>
</template>
